/**
 * Stage 5 — Geological Model (strict gate).
 *
 * Embeds the existing FlexLayout GeoModellingTab (ore-shell, explicit,
 * implicit, faults, validate). The backend `/geomodel/ore-shell` route
 * fires the Stage 5 pipeline-state hook automatically on success, so the
 * user just runs a shell build and the stepper flips to complete.
 */
import { useEffect, useState } from "react";
import GeoModellingTab from "@/components/tabs/GeoModellingTab";
import api from "@/lib/api";
import AIAutopilotPanel from "../AIAutopilotPanel";

export default function Stage5GeologicalModel({ projectId, onCompleted }) {
  // Poll pipeline-state briefly after mount so completions triggered
  // inside the embedded tab bubble up to the stepper without a manual reload.
  const [pollActive, setPollActive] = useState(false);
  useEffect(() => {
    if (!pollActive || !projectId) return undefined;
    const t = setInterval(() => onCompleted?.(), 3500);
    const stop = setTimeout(() => setPollActive(false), 60_000);
    return () => { clearInterval(t); clearTimeout(stop); };
  }, [pollActive, projectId, onCompleted]);

  return (
    <div data-testid="stage5-content" className="space-y-4">
      <AIAutopilotPanel
        projectId={projectId}
        title="AI Auto-Fill · Ore shell"
        description="Derive cutoff grade + RBF parameters directly from your uploaded assays and collar spacing, then build the ore-shell mesh in one click."
        autoFillPath="auto-fill/ore-shell"
        executePath={`/projects/${projectId}/geomodel/ore-shell`}
        buildPayload={(f) => ({
          cutoff_grade: f.cutoff_grade ?? 0.5,
          grid_shape: [f.grid_n ?? 28, f.grid_n ?? 28, Math.max(16, Math.round((f.grid_n ?? 28) * 0.6))],
          padding: f.padding ?? 30.0,
          epsilon: f.epsilon ?? 40.0,
          name: "ai_ore_shell",
        })}
        successLabel="Ore shell built — Stage 5 complete"
        buttonLabel="Build ore shell with AI values"
        onSuccess={() => { setPollActive(true); onCompleted?.(); }}
        testid="stage5-autopilot"
      />

      <div className="border border-neutral-200 bg-neutral-50 rounded p-3 text-[12px] text-neutral-800">
        <span className="text-[#4CAF50] font-medium">Manual mode:</span>{" "}
        prefer to configure each surface yourself? Use the tabs below (Explicit,
        Implicit, Ore shell, Faults, Validate). Completions here flip Stage 5 automatically.
        <button
          onClick={() => { setPollActive(true); onCompleted?.(); }}
          className="ml-3 text-[#4CAF50] hover:text-[#2E7D32] underline text-[11.5px]"
          data-testid="stage5-refresh-state">
          Refresh state
        </button>
      </div>
      <div className="border border-neutral-200 rounded-md bg-neutral-50 overflow-hidden">
        <GeoModellingTab projectId={projectId} />
      </div>
    </div>
  );
}
